/**
 * GymLog domain hooks: PowerSync queries with their canonical queryKey.
 *
 * Each hook encapsulates
 * queryKey (factory) + queryFn (Drizzle builder) + createPowerSyncQuery,
 * making it structurally impossible to duplicate subscriptions or
 * misalign key↔query.
 */

import type { Accessor } from "solid-js";
import { keepPreviousData } from "@tanstack/solid-query";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import type {
  BodyMeasurement,
  Exercise,
  PowerSyncDrizzleDB,
  Routine,
  WorkoutSession,
  WorkoutSet,
} from "@gymlog/powersync";

import {
  getBodyMeasurementsQuery,
  getExerciseHistoryQuery,
  getExercisesQuery,
  getOpenSessionQuery,
  getRoutineDetailQuery,
  getRoutineExercisesQuery,
  getRoutinesQuery,
  getSessionDetailQuery,
  getSessionSetsQuery,
  getSessionsQuery,
  getSetsSinceQuery,
  queryKeys,
  type RoutineExerciseWithMeta,
  type RoutineWithCount,
  type SessionSetWithMeta,
  type SessionWithStats,
  type WeeklySetRow,
} from "../index";

import {
  createPowerSyncQuery,
  createPowerSyncSingle,
  DEFAULT_THROTTLE_MS,
} from "./create-power-sync-query";

// ─── Shared option types ──────────────────────────────────────────────────────

interface BaseGymQueryOptions {
  db: Accessor<AbstractPowerSyncDatabase | null>;
  drizzle: Accessor<PowerSyncDrizzleDB | null>;
  userId: Accessor<string | undefined>;
  /** Stale time in ms. Pass your app's OFFLINE_CACHE_STALE_TIME_MS. */
  staleTime?: number;
  /** Optional extra throttle in ms. Defaults to 300ms. */
  throttleTime?: number;
}

interface ByIdGymQueryOptions {
  db: Accessor<AbstractPowerSyncDatabase | null>;
  drizzle: Accessor<PowerSyncDrizzleDB | null>;
  id: Accessor<string | undefined>;
  staleTime?: number;
  throttleTime?: number;
}

// ─── Exercises ───────────────────────────────────────────────────────────────

/** Visible exercise catalog (global + own, without archived). */
export function useExercisesQuery(
  options: BaseGymQueryOptions & { includeArchived?: Accessor<boolean> },
) {
  return createPowerSyncQuery<Exercise>({
    queryKey: () => queryKeys.gym.exercises(options.userId(), options.includeArchived?.()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid
        ? getExercisesQuery(db, uid, {
            includeArchived: options.includeArchived?.(),
          })
        : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

// ─── Routines ─────────────────────────────────────────────────────────────────

/** User's active routines with exercise counts. */
export function useRoutinesQuery(options: BaseGymQueryOptions) {
  return createPowerSyncQuery<RoutineWithCount>({
    queryKey: () => queryKeys.gym.routines(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getRoutinesQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

/** Details of a routine. */
export function useRoutineDetailQuery(options: ByIdGymQueryOptions) {
  return createPowerSyncSingle<Routine>({
    queryKey: () => queryKeys.gym.routineDetail(options.id()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const id = options.id();
      return db && id ? getRoutineDetailQuery(db, id) : null;
    },
    enabled: () => Boolean(options.id()) && Boolean(options.drizzle()),
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

/** Ordered exercises in a routine with metadata. */
export function useRoutineExercisesQuery(options: ByIdGymQueryOptions) {
  return createPowerSyncQuery<RoutineExerciseWithMeta>({
    queryKey: () => queryKeys.gym.routineExercises(options.id()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const id = options.id();
      return db && id ? getRoutineExercisesQuery(db, id) : null;
    },
    enabled: () => Boolean(options.id()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

// ─── Sessions ─────────────────────────────────────────────────────────────────

/** User's open session (or null). */
export function useOpenSessionQuery(options: BaseGymQueryOptions) {
  return createPowerSyncSingle<WorkoutSession>({
    queryKey: () => queryKeys.gym.openSession(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getOpenSessionQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

/** Session history with aggregated stats. */
export function useSessionsQuery(options: BaseGymQueryOptions & { limit: Accessor<number> }) {
  return createPowerSyncQuery<SessionWithStats>({
    queryKey: () => queryKeys.gym.sessions(options.userId(), options.limit()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getSessionsQuery(db, uid, options.limit()) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

/** Session details by id. */
export function useSessionDetailQuery(options: ByIdGymQueryOptions) {
  return createPowerSyncSingle<WorkoutSession>({
    queryKey: () => queryKeys.gym.sessionDetail(options.id()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const id = options.id();
      return db && id ? getSessionDetailQuery(db, id) : null;
    },
    enabled: () => Boolean(options.id()) && Boolean(options.drizzle()),
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

/** Sets in a session with exercise metadata. */
export function useSessionSetsQuery(options: ByIdGymQueryOptions) {
  return createPowerSyncQuery<SessionSetWithMeta>({
    queryKey: () => queryKeys.gym.sessionSets(options.id()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const id = options.id();
      return db && id ? getSessionSetsQuery(db, id) : null;
    },
    enabled: () => Boolean(options.id()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

// ─── History / summary / measurements ──────────────────────────────────────────

/** Complete set history for an exercise (PRs, warm-up, and charts). */
export function useExerciseHistoryQuery(
  options: BaseGymQueryOptions & { exerciseId: Accessor<string | undefined> },
) {
  return createPowerSyncQuery<WorkoutSet>({
    queryKey: () => queryKeys.gym.exerciseHistory(options.userId(), options.exerciseId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      const exerciseId = options.exerciseId();
      return db && uid && exerciseId ? getExerciseHistoryQuery(db, uid, exerciseId) : null;
    },
    enabled: () =>
      Boolean(options.userId()) && Boolean(options.exerciseId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

/** Sets since an ISO date with muscle group (weekly summary). */
export function useSetsSinceQuery(options: BaseGymQueryOptions & { sinceISO: Accessor<string> }) {
  return createPowerSyncQuery<WeeklySetRow>({
    queryKey: () => queryKeys.gym.setsSince(options.userId(), options.sinceISO()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getSetsSinceQuery(db, uid, options.sinceISO()) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

/** User's body measurements (most recent first). */
export function useBodyMeasurementsQuery(
  options: BaseGymQueryOptions & { limit?: Accessor<number | undefined> },
) {
  return createPowerSyncQuery<BodyMeasurement>({
    queryKey: () => queryKeys.gym.bodyMeasurements(options.userId(), options.limit?.()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getBodyMeasurementsQuery(db, uid, options.limit?.()) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}
